import React from "react";
import classNames from "classnames";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { useChampionProfileContext } from "@ugg/shared/components/Champions/ChampionProfileContext";
import { QueueTypeS, getQueueTypeName } from "@ugg/shared/utils/queue-type-helpers";
import { ChampionBalance } from "@ugg/shared/interfaces/champions/queue-type-balances.interface";
import { filterModStats } from "@ugg/shared/utils/champion-modification-helpers";

interface AdditionalStatsProps {
  className?: string;
  queueType: string;
  championModifications: ChampionBalance | undefined | null;
}

const AdditionalStats = (props: AdditionalStatsProps) => {
  const { className, queueType, championModifications } = props;
  const { getChampionSplashImg } = getRiotAssetsContext();
  const { championId, championData } = useChampionProfileContext();
  const queueTypeName = getQueueTypeName(queueType);
  const championName = championData?.name || "";
  const championSplashImg = getChampionSplashImg(championId);

  if (!championModifications) {
    return null;
  }

  const getLolWikiURL = () => {
    return queueType === QueueTypeS.ARURF || queueType === QueueTypeS.PICK_URF
      ? "https://leagueoflegends.fandom.com/wiki/Ultra_Rapid_Fire"
      : "https://leagueoflegends.fandom.com/wiki/ARAM";
  };

  const { damageDealt, championChanges, abilityChanges } = filterModStats(
    championModifications,
    championId,
    championData || undefined,
  );

  // Tailwind descendant variants replicate the former additional-stats.scss rules
  // for markup emitted by filterModStats (kept untouched as it is shared with champ-select).
  const modMarkupClasses = classNames(
    "[&_.bold-right]:font-bold [&_.bold-right]:mr-[4px] [&_.bold-right]:text-white",
    "[&_.bold-left]:font-bold [&_.bold-left]:ml-[4px] [&_.bold-left]:text-white",
    "[&_.ability]:mt-[4px] [&_.ability]:leading-[8px]",
    "[&_.ability_.image]:h-[24px] [&_.ability_.image]:w-[24px] [&_.ability_.image]:mr-[8px]",
    "[&_.ability_.name]:flex [&_.ability_.name]:items-center [&_.ability_.name]:py-[6px] [&_.ability_.name]:text-[14px] [&_.ability_.name]:font-bold [&_.ability_.name]:text-white",
    "[&_.bullets]:mt-[4px] [&_.bullets]:mr-[14px] [&_.bullets]:leading-5",
  );

  return (
    <div className={classNames("relative flex flex-col overflow-hidden bg-purple-400 rounded-[3px] p-[18px]", className)}>
      {/* Champion splash anchored to the right, faded into the module — matches the Builds page champion background */}
      <div className="pointer-events-none absolute inset-y-0 right-0 z-0 w-[394px] max-w-[40%]">
        <img className="h-full w-full object-cover object-[50%_30%] opacity-40" src={championSplashImg} alt="" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#191937_0%,rgba(25,25,55,0)_35%)]" />
      </div>
      <div className="relative z-[1] flex flex-1 flex-col">
        <HeaderBullet>
          {championName} {queueTypeName} Modifications
        </HeaderBullet>
        <div
          className={classNames(
            "flex flex-1 mt-[18px] text-[14px] text-[#bbbedb] font-bold font-['Inter'] overflow-auto",
            modMarkupClasses,
          )}
        >
          <ul className="flex-1 flex flex-col p-0 m-0 list-disc font-extralight">
            {[...damageDealt, ...championChanges].map((modification, idx) => {
              return (
                <div className="flex items-center" key={idx}>
                  <div className="flex-none h-[5px] w-[5px] mr-[7px] rounded-full bg-[#bbbedb]"></div>
                  {modification}
                </div>
              );
            })}
            {abilityChanges.map((modification, idx) => {
              return (
                <div className="flex items-center" key={idx}>
                  {modification}
                </div>
              );
            })}
            <div className="pt-[12px] mt-auto font-light text-[14px] leading-[20px] text-lavender-300">
              Stats collected from{" "}
              <a target="_blank" className="text-lavender-300 underline" href={getLolWikiURL()}>
                here
              </a>
            </div>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdditionalStats;
